import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

interface HomeworkItem {
  id: number;
  subject: string;
  title: string;
  description: string | null;
  dueDate: string | null;
  status: 'PENDING' | 'DONE';
}

const SUBJECTS = ['Math', 'Science', 'English', 'History', 'Art', 'PE', 'Other'];

const Homework: React.FC<{ kidId?: number | string }> = ({ kidId }) => {
  const { userId } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const [items, setItems] = useState<HomeworkItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: 'Math', title: '', description: '', dueDate: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'DONE'>('ALL');

  useEffect(() => { fetchHomework(); }, []);

  const fetchHomework = async () => {
    try {
      const res = await api.get(`/api/kids/${effectiveKidId}/homework`);
      setItems(res.data);
    } catch {}
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/api/kids/${effectiveKidId}/homework`, {
        ...form,
        dueDate: form.dueDate || null,
      });
      setForm({ subject: 'Math', title: '', description: '', dueDate: '' });
      setShowForm(false);
      fetchHomework();
    } catch {
      alert('Failed to add homework');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDone = async (id: number) => {
    try {
      await api.put(`/api/kids/${effectiveKidId}/homework/${id}/done`);
      fetchHomework();
    } catch {}
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this homework?')) return;
    try {
      await api.delete(`/api/kids/${effectiveKidId}/homework/${id}`);
      fetchHomework();
    } catch {}
  };

  const filtered = items.filter(i => filter === 'ALL' || i.status === filter);
  const pendingCount = items.filter(i => i.status === 'PENDING').length;

  return (
    <div>
      <main className="space-y-4">
        <h1 className="text-xl font-display font-extrabold text-gray-800">📚 Homework</h1>
        {/* Stats bar */}
        <div className="flex gap-3">
          {(['ALL', 'PENDING', 'DONE'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                filter === f ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-blue-50'
              }`}
            >
              {f === 'ALL' ? `All (${items.length})` : f === 'PENDING' ? `Pending (${pendingCount})` : `Done (${items.length - pendingCount})`}
            </button>
          ))}
        </div>

        {/* Add button */}
        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium transition"
        >
          + Add Homework
        </button>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow p-5">
            <form onSubmit={handleAdd} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">Subject</label>
                  <select
                    value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Due Date</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={e => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600">Title</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Chapter 5 exercises"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Any extra notes..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="text-sm text-gray-500 px-4 py-2">Cancel</button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Homework list */}
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <div className="text-4xl mb-2">📚</div>
            <p>{filter === 'DONE' ? 'No completed homework yet.' : 'No homework! Enjoy your free time 🎉'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(hw => (
              <div
                key={hw.id}
                className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${hw.status === 'DONE' ? 'border-green-400 opacity-70' : 'border-blue-400'}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{hw.subject}</span>
                      {hw.dueDate && (
                        <span className="text-xs text-gray-400">Due: {new Date(hw.dueDate).toLocaleDateString()}</span>
                      )}
                    </div>
                    <p className={`font-medium text-gray-800 ${hw.status === 'DONE' ? 'line-through text-gray-400' : ''}`}>
                      {hw.title}
                    </p>
                    {hw.description && <p className="text-xs text-gray-500 mt-1">{hw.description}</p>}
                  </div>
                  <div className="flex gap-2 ml-3">
                    {hw.status === 'PENDING' && (
                      <button
                        onClick={() => handleMarkDone(hw.id)}
                        className="text-green-500 hover:text-green-700 text-lg"
                        title="Mark as done"
                      >✓</button>
                    )}
                    <button
                      onClick={() => handleDelete(hw.id)}
                      className="text-red-400 hover:text-red-600 text-sm"
                      title="Delete"
                    >🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Homework;
